import { Frame, Toast, Form, FormLayout, TextField, Button, Spinner, DataTable, Card } from '@shopify/polaris';
import { useState, useCallback, useEffect } from 'react';
import api from '../../helpers/api';
import Api from "../../helpers/api";

const SetThreshold = () => {
    const [threshold, setThreshold] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);
    const [toastOptions, setToastOptions] = useState({
        active: false,
        message: '',
    });

    useEffect(() => {
        const fetchCurrentSettings = async () => {
            try {
                const { data } = await api.getThresholdAndGift();
                setThreshold(data.data.threshold || '');
                if (data.data.giftProduct) {
                    setSelectedProduct({
                        shopify_variant_id: data.data.giftProduct.id,
                        product_title: data.data.giftProduct.title,
                        sku: data.data.giftProduct.sku,
                    });
                }
            } catch (error) {
                setErrors({ fetch: 'Failed to fetch current settings. Try again later.' });
            }
        };

        fetchCurrentSettings();
    }, []);

    const handleThresholdChange = (value) => setThreshold(value);
    const handleSearchInputChange = (value) => setSearchInput(value);

    const handleSearchProducts = async () => {
        if (searchInput.length < 3) {
            setErrors({ search: 'Search term must be at least 3 characters long.' });
            return;
        }

        setSearchLoading(true);
        try {
            const { data } = await Api.getProducts(searchInput, false, 1);
            setSearchResults(data.data);
            setErrors({});
        } catch (error) {
            setErrors({ search: 'Failed to fetch products. Try again later.' });
        } finally {
            setSearchLoading(false);
        }
    };

    const handleSave = async () => {
        if (!threshold) {
            setErrors({ form: 'Threshold value required.' });
            return;
        }

        setLoading(true);
        try {
            await api.updateThresholdAndGift({
                threshold: parseFloat(threshold),
                giftProduct: selectedProduct ? selectedProduct.shopify_variant_id : null,
            });
            setToastOptions({
                active: true,
                message: 'Settings saved successfully.',
            });
            setErrors({});
        } catch (error) {
            setErrors({ form: 'Failed to save settings. Try again later.' });
        } finally {
            setLoading(false);
        }
    };

    const onToastDismiss = useCallback(() => {
        setToastOptions({ active: false, message: '' });
    }, []);

    return (
        <Frame>
            <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '20px' }}>
                <Form onSubmit={handleSave}>
                    <FormLayout>
                        <TextField
                            label="Threshold ($)"
                            value={threshold}
                            onChange={handleThresholdChange}
                            type="number"
                            min="0"
                            error={errors.threshold}
                        />
                        <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <strong>Current Gift Product:</strong>
                                {selectedProduct ? (
                                    <span>
                                        {` ${selectedProduct.product_title}`}
                                        {selectedProduct.sku && ` (${selectedProduct.sku})`}
                                    </span>
                                ) : (
                                    ' none'
                                )}
                            </div>
                            {selectedProduct && (
                                <Button size="micro" onClick={() => setSelectedProduct(null)}>Remove</Button>
                            )}
                        </div>
                        {!selectedProduct ? (
                            <>
                            <TextField
                                value={searchInput}
                                onChange={handleSearchInputChange}
                                placeholder="Search for a product..."
                                error={errors.search}
                            />
                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px', marginBottom: '10px'}}>
                                <Button onClick={handleSearchProducts} disabled={searchLoading} primary>
                                    {searchLoading ? <Spinner size="small" /> : 'Search'}
                                </Button>
                            </div>

                            {searchResults.length > 0 ? (
                                <Card title="Search Results" sectioned>
                                    <DataTable
                                        columnContentTypes={['text', 'text', 'text']}
                                        headings={[
                                            'Product Title',
                                            'SKU',
                                            <div style={{ textAlign: 'center' }}>Actions</div>,
                                        ]}
                                        rows={searchResults.map((product) => [
                                            product.product_title,
                                            product.sku,
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <Button size="slim" onClick={() => setSelectedProduct(product)}>
                                                    Select
                                                </Button>
                                            </div>,
                                        ])}
                                    />
                                </Card>
                            ) : (
                                <Card title="No Results Found" sectioned>
                                    <p style={{ textAlign: 'center', color: 'gray' }}>
                                        No products match your search criteria. Please try again.
                                    </p>
                                </Card>
                            )}

                            </>
                        ) : null}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '100px' }}>
                            <Button
                                submit
                                primary
                                loading={loading}
                                disabled={!threshold}
                            >
                                Save Settings
                            </Button>
                        </div>
                        {errors.form && <div style={{ color: 'red', marginTop: '10px' }}>{errors.form}</div>}
                        {errors.fetch && <div style={{ color: 'red', marginTop: '10px' }}>{errors.fetch}</div>}
                    </FormLayout>
                </Form>
                {toastOptions.active && (
                    <Toast content={toastOptions.message} onDismiss={onToastDismiss} />
                )}
            </div>
        </Frame>
    );
};

export default SetThreshold;
